/**
 * One way to call Loupe's API.
 *
 * Every call sends the session cookie, sends and reads JSON, and fails with an
 * {@link ApiError} that carries the HTTP status, so a caller can tell "signed
 * out" from "the server broke" without re-reading the response.
 *
 * New code uses this. Older pages still call `fetch` directly and move over as
 * they're reworked.
 */

/** The `error` field of a response body, when there is one. */
function errorField(body: unknown): string | null {
  return body && typeof body === 'object' && 'error' in body && typeof body.error === 'string' ? body.error : null;
}

/** A non-2xx response. `message` is the server's `error` field when it sent one. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** The server's own explanation, when it sent one, for showing to the customer. */
  get serverMessage(): string | null {
    return errorField(this.body);
  }

  /** The session is missing, expired, or not allowed this call. */
  get unauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/** True when `error` is an {@link ApiError} with this status. */
export function isApiError(error: unknown, status?: number): error is ApiError {
  return error instanceof ApiError && (status === undefined || error.status === status);
}

async function request<T>(method: string, path: string, body?: unknown, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (body !== undefined) headers.set('Content-Type', 'application/json');

  const res = await fetch(path, {
    ...init,
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // Empty and non-JSON bodies read as undefined rather than throwing.
  const data: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    throw new ApiError(res.status, errorField(data) ?? `Request failed (${res.status})`, data);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, init?: RequestInit) => request<T>('GET', path, undefined, init),
  post: <T>(path: string, body?: unknown, init?: RequestInit) => request<T>('POST', path, body, init),
  put: <T>(path: string, body?: unknown, init?: RequestInit) => request<T>('PUT', path, body, init),
  patch: <T>(path: string, body?: unknown, init?: RequestInit) => request<T>('PATCH', path, body, init),
  delete: <T>(path: string, init?: RequestInit) => request<T>('DELETE', path, undefined, init),
};
